import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Container, Row, Col, Card, Button, Alert, Badge, Dropdown, Modal } from 'react-bootstrap';
import {
  FaDatabase,
  FaPlus,
  FaEdit,
  FaEye,
  FaCog,
  FaList,
  FaLayerGroup,
  FaUsers,
  FaArrowRight,
  FaInfoCircle,
  FaTrash,
  FaBolt,
  FaBars,
  FaTimes,
  FaUsersCog,
  FaTh,
  FaTags,
  FaSitemap,
  FaChevronRight,
  FaChevronLeft,
  FaChevronDown,
  FaShieldAlt,
  FaThList,
  FaCheckCircle,
  FaCogs,
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import FieldManager from './FieldManager';
import LayoutManager from './LayoutManager';
import RoleManager from './RoleManager';
import ActionManager from './ActionManager';
import RecordViewManager from './RecordViewManager';
import ObjectValidationManager from './ObjectValidationManager';
import LineItemWizardConfigPanel from './LineItemWizardConfigPanel';
import EditObject from '../layout/EditObject';
import './css/ObjectManager.css';
import { getEntitlementsSection } from '../../utils/entitlements';
import { filterObjectManagerAllowedObjects } from '../../utils/filterObjectManagerAllowedObjects';
import {
  getStandardObjectStatusLabel,
  canPublishStandardObject,
  canEditStandardObjectCatalog,
} from '../../utils/standardObjectPublishing';
import { useProductBuilderMode } from '../../utils/productBuilderMode';
import StandardObjectStatusBadge from './StandardObjectStatusBadge';
import PublishStandardObjectModal from './PublishStandardObjectModal';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';

/** New objects use the create-object API for standard fields and DDL (includes is_deleted for future soft delete / recycle bin); base_price is intentionally omitted for new objects (see backend objectconfig.model / schema.model). */

/** Resolve allow_explore from config, with schema/tenant override. Config may be: { allow_explore: false, demo: { allow_explore: true } }. */
const isEntitlementEnabled = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

/** Backend trashObject rejects these system objects regardless of entitlements. */
const SYSTEM_OBJECTS_NO_TRASH = new Set([
  'user',
  'company',
  'object_config',
  'object_config_fields',
]);

const renderObjectReferenceSections = (references = {}, objectName = 'This object') => (
  <>
    <Alert variant="warning">
      <strong>{objectName}</strong>{' '}
      cannot be deleted because it is referenced in other configurations. Please remove the references below and
      try again.
    </Alert>
    {(() => {
      const workflowRefs = references.workflows || [];
      const validationRefs = references.validations || [];
      const reportRefs = references.reports || [];
      const lookupRefs = references.lookupFields || [];
      const rollupRefs = references.rollupFields || [];
      const calculatedRefs = references.crossObjectCalculatedFields || [];
      const sections = [];

      if (workflowRefs.length > 0) {
        sections.push(
          <div key="workflows">
            <h6 className="text-dark mt-3">Workflows</h6>
            <ul className="ps-3">
              {workflowRefs.map((workflow) => (
                <li key={workflow.workflow_id}>
                  <strong>{workflow.workflow_name || 'Untitled Workflow'}</strong>
                </li>
              ))}
            </ul>
          </div>
        );
      }

      if (validationRefs.length > 0) {
        sections.push(
          <div key="validations">
            <h6 className="text-dark mt-3">Validation Rules</h6>
            <ul className="ps-3">
              {validationRefs.map((rule) => (
                <li key={rule.validation_id}>
                  <strong>{rule.validation_name || 'Untitled Validation'}</strong>
                </li>
              ))}
            </ul>
          </div>
        );
      }

      if (reportRefs.length > 0) {
        sections.push(
          <div key="reports">
            <h6 className="text-dark mt-3">Reports</h6>
            <ul className="ps-3">
              {reportRefs.map((report) => (
                <li key={report.report_id}>
                  <strong>{report.report_name || 'Untitled Report'}</strong>
                </li>
              ))}
            </ul>
          </div>
        );
      }

      if (lookupRefs.length > 0) {
        sections.push(
          <div key="lookups">
            <h6 className="text-dark mt-3">Lookup Fields</h6>
            <ul className="ps-3">
              {lookupRefs.map((field) => (
                <li key={field.field_id}>
                  <strong>{field.field_label || field.field_name}</strong>
                  <div className="text-muted small">
                    Object: {field.parent_object_label || field.parent_object_name}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      }

      if (rollupRefs.length > 0) {
        sections.push(
          <div key="rollups">
            <h6 className="text-dark mt-3">Roll-Up Summary Fields</h6>
            <ul className="ps-3">
              {rollupRefs.map((field) => (
                <li key={field.field_id}>
                  <strong>{field.field_label || field.field_name}</strong>
                  <div className="text-muted small">
                    Object: {field.parent_object_label || field.parent_object_name}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      }

      if (calculatedRefs.length > 0) {
        sections.push(
          <div key="calculated">
            <h6 className="text-dark mt-3">Calculated Fields</h6>
            <ul className="ps-3">
              {calculatedRefs.map((ref) => (
                <li key={`${ref.id}-${ref.referenced_field_name || 'field'}`}>
                  <strong>{ref.field_label || ref.field_name}</strong>
                  <div className="text-muted small">
                    Object: {ref.parent_object_label || ref.parent_object_name}
                    {ref.lookup_field_name && ref.referenced_field_name && (
                      <> · via {ref.lookup_field_name}.{ref.referenced_field_name}</>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      }

      if (sections.length === 0) {
        return (
          <p className="mb-0 text-muted">
            This object is still referenced elsewhere. Please review workflows, validation rules, reports, lookup fields, or calculated fields.
          </p>
        );
      }

      return sections;
    })()}
  </>
);

const ObjectManager = () => {
  const location = window.location;
  const objectManagerConfig = getEntitlementsSection('objectManager', {});
  const allowObjectCreate =
    objectManagerConfig?.allow_create ??
    objectManagerConfig?.allowCreate ??
    true;
  // Full standard object config (activity, is_standard flag, etc.) when enable_standard is explicitly true
  const enableStandardObject =
    isEntitlementEnabled(objectManagerConfig?.enable_standard) ||
    isEntitlementEnabled(objectManagerConfig?.enableStandard);
  /** Object label / plural always editable via Edit Object; EditObject limits other fields when !enableStandardObject */
  const canEditObject = (object) => Boolean(object);
  /** Custom objects always trashable; standard objects when Enable Standard is on (same as standard layout delete). */
  const canTrashObject = (object) => {
    if (!object) return false;
    const name = String(object.object_name || '').toLowerCase();
    if (SYSTEM_OBJECTS_NO_TRASH.has(name)) return false;
    if (!object.is_standard) return true;
    return enableStandardObject;
  };
  const [rawObjectsWithFields, setRawObjectsWithFields] = useState([]);
  const productBuilderMode = useProductBuilderMode();
  const objects = useMemo(
    () =>
      filterObjectManagerAllowedObjects(rawObjectsWithFields, { productBuilderMode }).map((obj) => ({
        ...obj,
        field_count: obj.fields ? obj.fields.length : 0,
        layout_count: 0,
        record_count: 0,
      })),
    [rawObjectsWithFields, productBuilderMode]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedObject, setSelectedObject] = useState(null);
  const [activeView, setActiveView] = useState('objects'); // objects, object-details, fields, layouts, recordviews, actions, validations
  const [showFieldManager, setShowFieldManager] = useState(false);
  const [showRoleManager, setShowRoleManager] = useState(false);
  const [showEditObject, setShowEditObject] = useState(false);
  const [editObjectMode, setEditObjectMode] = useState('create');
  const [editObjectData, setEditObjectData] = useState(null);
  const [trashedObjects, setTrashedObjects] = useState([]);
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [loadingTrashedObjects, setLoadingTrashedObjects] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoringFromUrl, setRestoringFromUrl] = useState(false);
  const [sortBy, setSortBy] = useState('name'); // 'name', 'label', 'type', 'created'
  const [sortOrder, setSortOrder] = useState('asc');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // 'asc', 'desc'
  const [isSorting, setIsSorting] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  /** When set, LayoutManager opens this layout once (deep link from bulk assignment page). */
  const [pendingLayoutOpenId, setPendingLayoutOpenId] = useState(null);
  const [showObjectTrashModal, setShowObjectTrashModal] = useState(false);
  const [objectToTrash, setObjectToTrash] = useState(null);
  const [objectTrashModalChecking, setObjectTrashModalChecking] = useState(false);
  const [objectTrashModalDeleting, setObjectTrashModalDeleting] = useState(false);
  const [objectTrashModalError, setObjectTrashModalError] = useState(null);
  const [objectTrashModalBlockedReferences, setObjectTrashModalBlockedReferences] = useState(null);
  const [showObjectPermanentDeleteModal, setShowObjectPermanentDeleteModal] = useState(false);
  const [objectToPermanentlyDelete, setObjectToPermanentlyDelete] = useState(null);
  const [objectPermanentDeleteModalChecking, setObjectPermanentDeleteModalChecking] = useState(false);
  const [objectPermanentDeleteModalDeleting, setObjectPermanentDeleteModalDeleting] = useState(false);
  const [objectPermanentDeleteModalError, setObjectPermanentDeleteModalError] = useState(null);
  const [objectPermanentDeleteModalBlockedReferences, setObjectPermanentDeleteModalBlockedReferences] = useState(null);
  const objectDeleteFlowActiveRef = useRef(false);
  const [objectDeleteFlowBusy, setObjectDeleteFlowBusy] = useState(false);

  const isObjectDeleteBusy =
    objectDeleteFlowBusy ||
    objectTrashModalChecking ||
    objectTrashModalDeleting ||
    showObjectTrashModal ||
    objectPermanentDeleteModalChecking ||
    objectPermanentDeleteModalDeleting ||
    showObjectPermanentDeleteModal;
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view');
    const objectNameParam = urlParams.get('object');
    const layoutIdParam = urlParams.get('layoutId');

    console.log('ObjectManager - URL params:', { viewParam, objectNameParam, layoutIdParam });

    if (viewParam && objectNameParam) {
      setRestoringFromUrl(true);
      const findObjectByName = async () => {
        try {
          const api = new DataApi('objectconfig');
          const response = await api.fetchAll();

          if (response?.data?.success) {
            const objectsWithFields = response.data.data || [];
            const visibleObjects = filterObjectManagerAllowedObjects(objectsWithFields, {
              productBuilderMode,
            });
            const searchPool = productBuilderMode ? objectsWithFields : visibleObjects;
            const foundObject = searchPool.find(
              (obj) => obj.object_name === objectNameParam && !obj.is_deleted
            );

            console.log('ObjectManager - Found object from URL:', foundObject);

            if (foundObject) {
              setSelectedObject(foundObject);
              setActiveView(viewParam);
              if (layoutIdParam) {
                setPendingLayoutOpenId(layoutIdParam);
              }
              console.log('ObjectManager - State restored from URL:', { object: foundObject.object_name, view: viewParam });
            } else {
              console.log('ObjectManager - Object not found or not explorable, clearing URL');
              const url = new URL(window.location);
              url.searchParams.delete('view');
              url.searchParams.delete('object');
              url.searchParams.delete('layoutId');
              window.history.replaceState({}, '', url);
            }
          }
        } catch (error) {
          console.error('Error restoring state from URL:', error);
          const url = new URL(window.location);
          url.searchParams.delete('view');
          url.searchParams.delete('object');
          url.searchParams.delete('layoutId');
          window.history.replaceState({}, '', url);
        } finally {
          setRestoringFromUrl(false);
        }
      };

      findObjectByName();
    }
  }, [productBuilderMode]);


  useEffect(() => {
    if (activeView !== 'objects' && selectedObject) {
      const url = new URL(window.location);
      url.searchParams.set('view', activeView);
      url.searchParams.set('object', selectedObject.object_name);
      if (pendingLayoutOpenId) {
        url.searchParams.set('layoutId', pendingLayoutOpenId);
      } else {
        url.searchParams.delete('layoutId');
      }
      window.history.replaceState({}, '', url);
      console.log('ObjectManager - URL updated:', url.toString());
    } else {

      const url = new URL(window.location);
      url.searchParams.delete('view');
      url.searchParams.delete('object');
      url.searchParams.delete('layoutId');
      window.history.replaceState({}, '', url);
      console.log('ObjectManager - URL cleared');
    }
  }, [activeView, selectedObject, pendingLayoutOpenId]);

  useEffect(() => {
    fetchObjects();
    fetchTrashedObjects(); // Load trashed objects count on page load
  }, []);

  const fetchObjects = async () => {
    try {
      setLoading(true);
      setError('');

      // Call the existing endpoint that fetches all objects with fields
      const api = new DataApi('objectconfig');
      const response = await api.fetchAll();

      console.log('ObjectManager API Response:', response);

      if (response?.data?.success) {
        const objectsWithFields = response.data.data || [];
        console.log('ObjectManager - Raw API response:', response.data);
        console.log('ObjectManager - First object structure:', objectsWithFields[0]);
        setRawObjectsWithFields(objectsWithFields);
      } else {
        setRawObjectsWithFields([]);
      }

    } catch (error) {
      console.error('Error fetching objects:', error);
      setRawObjectsWithFields([]);
      setError('Failed to load objects: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleObjectSelect = (object) => {
    setSelectedObject(object);
    setActiveView('object-details');
  };

  const handleViewChange = (view) => {
    setActiveView(view);
  };

  const handleCreateObject = () => {
    if (!allowObjectCreate) {
      setError('Object creation is disabled for this tenant.');
      return;
    }
    setEditObjectMode('create');
    setEditObjectData(null);
    setShowEditObject(true);
  };

  const handleEditObject = (object) => {
    setEditObjectMode('edit');
    setEditObjectData(object);
    setShowEditObject(true);
  };

  const handlePublishSuccess = (publishData) => {
    const objectName = selectedObject?.object_name;
    if (!objectName) return;

    const updatedStatus = publishData?.objectConfig?.standard_status
      || selectedObject?.standard_status
      || 'published';

    setSelectedObject((prev) => (
      prev ? { ...prev, standard_status: updatedStatus } : prev
    ));
    setRawObjectsWithFields((prev) =>
      prev.map((obj) => (
        obj.object_name === objectName
          ? { ...obj, standard_status: updatedStatus }
          : obj
      ))
    );
    const wasPublish = String(selectedObject?.standard_status || '').toLowerCase() === 'draft';
    setSuccess(
      wasPublish
        ? 'Standard object published successfully!'
        : 'Catalog settings saved successfully!'
    );
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleEditObjectSuccess = (createdObjectData) => {
    fetchObjects(); // Refresh the objects list

    // If this was a new object creation, immediately navigate to object detail page
    if (editObjectMode === 'create' && createdObjectData) {
      // Ensure the object has the required properties for handleObjectSelect
      const objectForSelection = {
        object_name: createdObjectData.objectName || createdObjectData.object_name || createdObjectData.name,
        object_label: createdObjectData.objectLabel || createdObjectData.object_label || createdObjectData.label,
        plural_label: createdObjectData.pluralLabel || createdObjectData.plural_label,
        id: createdObjectData.id || createdObjectData.objectConfig?.data?.id,
        field_count: 0,
        layout_count: 0,
        record_count: 0
      };

      console.log('ObjectManager - Created object data:', createdObjectData);
      console.log('ObjectManager - Object for selection:', objectForSelection);

      handleObjectSelect(objectForSelection);
    }
  };

  const fetchTrashedObjects = async () => {
    try {
      setLoadingTrashedObjects(true);
      const response = await ObjectConfigApi.getTrashedObjects();

      if (response?.success) {
        setTrashedObjects(response.data || []);
      } else {
        setError(response?.message || 'Failed to fetch trashed objects');
      }
    } catch (error) {
      console.error('Error fetching trashed objects:', error);
      setError('Failed to fetch trashed objects: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingTrashedObjects(false);
    }
  };

  const resetObjectTrashModal = () => {
    setShowObjectTrashModal(false);
    setObjectToTrash(null);
    setObjectTrashModalChecking(false);
    setObjectTrashModalDeleting(false);
    setObjectTrashModalError(null);
    setObjectTrashModalBlockedReferences(null);
    objectDeleteFlowActiveRef.current = false;
    setObjectDeleteFlowBusy(false);
  };

  const resetObjectPermanentDeleteModal = () => {
    setShowObjectPermanentDeleteModal(false);
    setObjectToPermanentlyDelete(null);
    setObjectPermanentDeleteModalChecking(false);
    setObjectPermanentDeleteModalDeleting(false);
    setObjectPermanentDeleteModalError(null);
    setObjectPermanentDeleteModalBlockedReferences(null);
    objectDeleteFlowActiveRef.current = false;
    setObjectDeleteFlowBusy(false);
  };

  const handleTrashObject = async (objectName) => {
    if (!objectName || objectDeleteFlowActiveRef.current) {
      return;
    }

    objectDeleteFlowActiveRef.current = true;
    setObjectDeleteFlowBusy(true);
    setObjectToTrash(objectName);
    setObjectTrashModalError(null);
    setObjectTrashModalBlockedReferences(null);
    setObjectTrashModalChecking(true);
    setShowObjectTrashModal(true);

    try {
      const referenceResponse = await ObjectConfigApi.getObjectReferences(objectName);
      if (referenceResponse && referenceResponse.success === false) {
        throw new Error(referenceResponse.message || 'Failed to check object references');
      }

      const referenceData = referenceResponse?.data || referenceResponse;
      if (referenceData?.hasReferences) {
        setObjectTrashModalBlockedReferences(referenceData.references || {});
      }
    } catch (error) {
      console.error('Error checking object references:', error);
      setObjectTrashModalError(error.message || 'Failed to check object references');
    } finally {
      setObjectTrashModalChecking(false);
      objectDeleteFlowActiveRef.current = false;
      setObjectDeleteFlowBusy(false);
    }
  };

  const confirmTrashObject = async () => {
    if (!objectToTrash || objectTrashModalDeleting) return;

    setObjectTrashModalError(null);
    setObjectTrashModalDeleting(true);

    try {
      const response = await ObjectConfigApi.trashObject(objectToTrash);

      if (response?.success) {
        setSuccess('Object moved to trash successfully!');
        setTimeout(() => setSuccess(''), 3000);

        if (selectedObject && selectedObject.object_name === objectToTrash) {
          setSelectedObject(null);
          setActiveView('objects');
        }

        await fetchObjects();
        fetchTrashedObjects();
        resetObjectTrashModal();
      } else if (response?.details?.hasReferences) {
        setObjectTrashModalBlockedReferences(response.details.references || {});
      } else {
        setObjectTrashModalError(response?.message || 'Failed to move object to trash');
      }
    } catch (error) {
      console.error('Error trashing object:', error);
      setObjectTrashModalError(error.message || 'Failed to move object to trash');
    } finally {
      setObjectTrashModalDeleting(false);
    }
  };

  const handlePermanentlyDeleteObject = async (objectName) => {
    if (!objectName || objectDeleteFlowActiveRef.current) {
      return;
    }

    objectDeleteFlowActiveRef.current = true;
    setObjectDeleteFlowBusy(true);
    setObjectToPermanentlyDelete(objectName);
    setObjectPermanentDeleteModalError(null);
    setObjectPermanentDeleteModalBlockedReferences(null);
    setObjectPermanentDeleteModalChecking(true);
    setShowObjectPermanentDeleteModal(true);

    try {
      const referenceResponse = await ObjectConfigApi.getObjectReferences(objectName);
      if (referenceResponse && referenceResponse.success === false) {
        throw new Error(referenceResponse.message || 'Failed to check object references');
      }

      const referenceData = referenceResponse?.data || referenceResponse;
      if (referenceData?.hasReferences) {
        setObjectPermanentDeleteModalBlockedReferences(referenceData.references || {});
      }
    } catch (error) {
      console.error('Error checking object references:', error);
      setObjectPermanentDeleteModalError(error.message || 'Failed to check object references');
    } finally {
      setObjectPermanentDeleteModalChecking(false);
      objectDeleteFlowActiveRef.current = false;
      setObjectDeleteFlowBusy(false);
    }
  };

  const confirmPermanentlyDeleteObject = async () => {
    if (!objectToPermanentlyDelete || objectPermanentDeleteModalDeleting) return;

    setObjectPermanentDeleteModalError(null);
    setObjectPermanentDeleteModalDeleting(true);

    try {
      const response = await ObjectConfigApi.permanentlyDeleteObject(objectToPermanentlyDelete);

      if (response?.success) {
        setTrashedObjects(trashedObjects.filter(obj => obj.object_name !== objectToPermanentlyDelete));
        setSuccess('Object permanently deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
        fetchTrashedObjects();
        if (trashedObjects.length <= 1) {
          setShowTrashModal(false);
        }
        resetObjectPermanentDeleteModal();
      } else if (response?.details?.hasReferences) {
        setObjectPermanentDeleteModalBlockedReferences(response.details.references || {});
      } else {
        setObjectPermanentDeleteModalError(response?.message || 'Failed to permanently delete object');
      }
    } catch (error) {
      console.error('Error permanently deleting object:', error);
      setObjectPermanentDeleteModalError(error.message || 'Failed to permanently delete object');
    } finally {
      setObjectPermanentDeleteModalDeleting(false);
    }
  };

  const handleRestoreObject = async (objectName) => {
    try {
      setLoadingTrashedObjects(true);

      const response = await ObjectConfigApi.restoreObject(objectName);

      if (response?.success) {
        setTrashedObjects(trashedObjects.filter(obj => obj.object_name !== objectName));
        setSuccess('Object restored successfully!');
        setTimeout(() => setSuccess(''), 3000);

        // Refresh main objects list
        fetchObjects();

        // Refresh trashed objects count immediately
        fetchTrashedObjects();

        // Close modal if no more trashed objects
        if (trashedObjects.length <= 1) {
          setShowTrashModal(false);
        }
      } else {
        setError(response?.message || 'Failed to restore object');
      }
    } catch (error) {
      console.error('Error restoring object:', error);
      setError('Failed to restore object: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingTrashedObjects(false);
    }
  };


  const getObjectIcon = (objectName) => {
    if (!objectName) return '📄';

    const icons = {
      contact: '👤',
      lead: '🎯',
      account: '🏢',
      opportunity: '💰',
      task: '📋',
      case: '📞',
      product: '📦',
      pricebook: '📊',
      user: '👨‍💼',
      role: '🔐'
    };
    return icons[objectName.toLowerCase()] || '📄';
  };

  const handleSortChange = (newSortBy) => {
    setIsSorting(true);

    if (sortBy === newSortBy) {
      // If clicking the same sort field, toggle order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different sort field, set to ascending
      setSortBy(newSortBy);
      setSortOrder('asc');
    }

    // Reset sorting state after a brief delay
    setTimeout(() => setIsSorting(false), 100);
  };

  // Filter and sort objects
  const filteredObjects = objects
    .filter(object => {
      const term = searchTerm.toLowerCase();
      const typeLabel = object.is_standard ? 'standard' : 'custom';
      const statusLabel = getStandardObjectStatusLabel(object, productBuilderMode)?.toLowerCase() || '';
      return object.object_name.toLowerCase().includes(term) ||
        (object.object_label && object.object_label.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (object.description && object.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        typeLabel.includes(term) ||
        (statusLabel && statusLabel.includes(term));
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = a.object_name.toLowerCase();
          bValue = b.object_name.toLowerCase();
          break;
        case 'label':
          aValue = (a.object_label || a.object_name).toLowerCase();
          bValue = (b.object_label || b.object_name).toLowerCase();
          break;
        case 'created':
          aValue = new Date(a.createddate || 0);
          bValue = new Date(b.createddate || 0);
          break;
        case 'type':
          aValue = a.is_standard ? 'standard' : 'custom';
          bValue = b.is_standard ? 'standard' : 'custom';
          break;
        default:
          aValue = a.object_name.toLowerCase();
          bValue = b.object_name.toLowerCase();
      }

      if (sortBy === 'created') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }
    });



  const renderObjectsView = () => (
    <div className="objects-view">
      <div className="view-header">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h2>
              <FaDatabase className="me-3" />
              Object Manager
            </h2>
            <p className="text-muted">
              Manage your CRM objects, fields, layouts, and roles
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setShowTrashModal(true);
                if (trashedObjects.length === 0) {
                  fetchTrashedObjects();
                }
              }}
            >
              <FaTrash className="me-1" />
              View Trash
              {trashedObjects.length > 0 && (
                <Badge bg="danger" className="ms-1">
                  {trashedObjects.length}
                </Badge>
              )}
            </Button>
            {allowObjectCreate && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleCreateObject}
              >
                <FaPlus className="me-1" />
                Create New Object
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Search Header - Always Visible */}
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaList className="me-2" />
              CRM Objects ({filteredObjects.length} of {objects.length})
              {sortBy && (
                <small className="text-muted ms-2">
                  - Sorted by {sortBy === 'name' ? 'Name' : sortBy === 'label' ? 'Label' : sortBy === 'type' ? 'Type' : 'Created Date'} ({sortOrder === 'asc' ? 'A-Z' : 'Z-A'})
                  {isSorting && <span className="ms-1">⏳</span>}
                </small>
              )}
            </h5>
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0 small text-white">Search:</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search objects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '200px' }}
              />
              {searchTerm && (
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => setSearchTerm('')}
                >
                  Clear
                </Button>
              )}
              <label className="form-label mb-0 small text-white">Sort by:</label>
              <select
                className="form-select form-select-sm"
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                style={{ width: '120px' }}
              >
                <option value="name">Name</option>
                <option value="label">Label</option>
                <option value="type">Type</option>
                <option value="created">Created</option>
              </select>
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </Button>
              {(sortBy !== 'name' || sortOrder !== 'asc') && (
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => {
                    setSortBy('name');
                    setSortOrder('asc');
                  }}
                  title="Reset to default sort (Name A-Z)"
                >
                  Reset
                </Button>
              )}
              <Dropdown className="ms-2">
                <Dropdown.Toggle variant="outline-light" size="sm" id="view-mode-dropdown">
                  {viewMode === 'table' ? <FaList /> : <FaTh />}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setViewMode('table')}>
                    <FaList className="me-2" />
                    Table View
                    {viewMode === 'table' && <span className="ms-2">✓</span>}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setViewMode('card')}>
                    <FaTh className="me-2" />
                    Card View
                    {viewMode === 'card' && <span className="ms-2">✓</span>}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Card.Header>
      </Card>

      {loading ? (
        <div className="text-center py-5">
          <div className="loading-spinner mb-3"></div>
          <p>Loading objects...</p>
        </div>
      ) : filteredObjects.length === 0 ? (
        <div className="text-center py-5">
          <div className="empty-state">
            <Card>
              <Card.Body>
                <h6>No Objects Found</h6>
                <p className="text-muted">
                  {objects.length === 0
                    ? 'No CRM objects have been configured yet. Contact your administrator to set up objects.'
                    : 'No objects match your search criteria. Try adjusting your search term.'
                  }
                </p>
              </Card.Body>
            </Card>
          </div>
        </div>
      ) : viewMode === 'table' ? (
        <div className="om-table-wrapper">
          <table className="om-data-table">
            <thead>
              <tr>
                <th style={{ width: '45%' }}>
                  <div className="om-th-content">
                    Object Label
                    <FaChevronDown className="om-sort-icon" />
                  </div>
                </th>
                <th style={{ width: '20%' }}>
                  <div className="om-th-content">
                    API Name
                    <FaChevronDown className="om-sort-icon" />
                  </div>
                </th>
                <th style={{ width: '15%' }}>
                  <div className="om-th-content">
                    Type
                    <FaChevronDown className="om-sort-icon" />
                  </div>
                </th>
                <th style={{ width: '20%' }}>
                  <div className="om-th-content">
                    Actions
                    <FaChevronDown className="om-sort-icon" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredObjects.map((object, index) => (
                <tr
                  key={object.id}
                  className="om-table-row"
                  onClick={() => handleObjectSelect(object)}
                >
                  <td>
                    <div className="om-object-cell">
                      <span className="om-obj-icon">{getObjectIcon(object.object_name)}</span>
                      <div className="om-obj-info">
                        <div className="om-obj-label">{object.object_label || object.object_name}</div>
                        <div className="om-obj-desc">
                          {object.description || `Manage ${object.object_label || object.object_name} data`}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <Badge bg="light" text="dark" className="me-1">
                      {object.object_name}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex flex-column align-items-start gap-1">
                      <span className="om-type-badge">{object.is_standard ? 'Standard' : 'Custom'}</span>
                      <StandardObjectStatusBadge object={object} />
                    </div>
                  </td>
                  <td>
                    <div className="om-action-btns">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="om-manage-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleObjectSelect(object);
                        }}
                      >
                        <FaCog className="me-1" />
                        Manage
                      </Button>
                      {canEditObject(object) && (
                        <Button
                          variant="link"
                          size="sm"
                          className="om-icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditObject(object);
                          }}
                          title="Edit"
                        >
                          <FaEdit />
                        </Button>
                      )}
                      {canTrashObject(object) && (
                        <Button
                          variant="link"
                          size="sm"
                          className="om-icon-btn om-delete-btn"
                          disabled={isObjectDeleteBusy}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTrashObject(object.object_name);
                          }}
                          title={object.is_standard ? 'Delete standard object' : 'Delete'}
                        >
                          <FaTrash />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Card>
          <Card.Body className="p-0">
            <div className="object-list">
              {filteredObjects.map((object, index) => (
                <div
                  key={object.id}
                  className={`object-list-item ${index === objects.length - 1 ? 'last-item' : ''}`}
                  onClick={() => handleObjectSelect(object)}
                >
                  <div className="object-list-content">
                    <div className="object-info">
                      <div className="object-icon-wrapper">
                        <span className="object-icon">{getObjectIcon(object.object_name)}</span>
                      </div>
                      <div className="object-details">
                        <h6 className="object-name">{object.object_label || object.object_name}</h6>
                        <p className="object-description">
                          {object.description || `Manage ${object.object_label || object.object_name} data`}
                        </p>
                        <div className="object-meta">
                          <Badge bg="secondary" className="me-2">{object.object_name}</Badge>
                          <StandardObjectStatusBadge object={object} className="me-2" />
                        </div>
                      </div>
                    </div>

                    <div className="object-actions">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleObjectSelect(object);
                        }}
                      >
                        <FaCog className="me-1" />
                        Manage
                      </Button>
                      {canEditObject(object) && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditObject(object);
                          }}
                        >
                          <FaEdit className="me-1" />
                          Edit
                        </Button>
                      )}
                      {canTrashObject(object) && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={isObjectDeleteBusy}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTrashObject(object.object_name);
                          }}
                        >
                          <FaTrash className="me-1" />
                          Trash
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );

  const renderObjectDetailsView = () => (
    <div className="object-details-view">
      <div className="view-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setSelectedObject(null);
                setActiveView('objects');
              }}
              className="mb-3"
            >
              ← Back to Objects
            </Button>
            <h2>
              <span className="object-icon me-2">{getObjectIcon(selectedObject.object_name)}</span>
              {selectedObject.object_label || selectedObject.object_name}
            </h2>
            {selectedObject.is_standard && (
              <div className="mb-2">
                <StandardObjectStatusBadge object={selectedObject} />
              </div>
            )}
            <p className="text-muted">
              Choose what you want to manage for this object
            </p>
          </div>
        </div>
      </div>

      {/* Object Information Card - First */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <FaInfoCircle className="me-2" />
                  Object Information
                </h5>
                <div className="d-flex gap-2">
                  {canPublishStandardObject(selectedObject, productBuilderMode) && (
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => setShowPublishModal(true)}
                    >
                      <FaCheckCircle className="me-1" />
                      Publish Standard Object
                    </Button>
                  )}
                  {canEditStandardObjectCatalog(selectedObject, productBuilderMode) && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setShowPublishModal(true)}
                    >
                      <FaCogs className="me-1" />
                      Edit Catalog Settings
                    </Button>
                  )}
                  {canEditObject(selectedObject) && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleEditObject(selectedObject)}
                    >
                      <FaEdit className="me-1" />
                      Edit Object
                    </Button>
                  )}
                  {canTrashObject(selectedObject) && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={isObjectDeleteBusy}
                      onClick={() => handleTrashObject(selectedObject.object_name)}
                    >
                      <FaTrash className="me-1" />
                      Delete Object
                    </Button>
                  )}
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="info-item">
                    <strong>Object Name:</strong> {selectedObject.object_name}
                  </div>
                  <div className="info-item">
                    <strong>Object Label:</strong> {selectedObject.object_label || 'N/A'}
                  </div>
                  <div className="info-item">
                    <strong>Plural Label:</strong> {selectedObject.plural_label || 'N/A'}
                  </div>
                  <div className="info-item">
                    <strong>Description:</strong> {selectedObject.description || 'No description available'}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="info-item">
                    <strong>Standard Object:</strong> {selectedObject.is_standard ? 'Yes' : 'No'}
                  </div>
                  {selectedObject.is_standard && getStandardObjectStatusLabel(selectedObject, productBuilderMode) && (
                    <div className="info-item">
                      <strong>Publishing Status:</strong>{' '}
                      <StandardObjectStatusBadge object={selectedObject} />
                    </div>
                  )}
                  <div className="info-item">
                    <strong>Activity Enabled:</strong> {selectedObject.allow_activity ? 'Yes' : 'No'}
                  </div>
                  <div className="info-item">
                    <strong>Created:</strong> {new Date(selectedObject.createddate).toLocaleDateString()}
                  </div>
                  <div className="info-item">
                    <strong>Last Modified:</strong> {new Date(selectedObject.lastmodifieddate).toLocaleDateString()}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Management Option Cards — compact: icon, title, one-line hint, primary action */}
      <Row>
        <Col lg={3} md={6} className="mb-4">
          <Card className="management-option-card" onClick={() => handleViewChange('fields')}>
            <Card.Body className="text-center py-3">
              <div className="management-icon-wrapper">
                <FaList size={40} className="text-primary" />
              </div>
              <h5 className="mt-2 mb-2">Field Management</h5>
              <p className="text-muted small mb-2 px-1">
                Create and organize fields, types, and field properties.
              </p>
              <Button variant="primary" className="mt-1 w-100">
                <FaList className="me-2" />
                Manage Fields
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-4">
          <Card className="management-option-card" onClick={() => handleViewChange('layouts')}>
            <Card.Body className="text-center py-3">
              <div className="management-icon-wrapper">
                <FaLayerGroup size={40} className="text-primary" />
              </div>
              <h5 className="mt-2 mb-2">Layout Management</h5>
              <p className="text-muted small mb-2 px-1">
                Page layouts, sections, and how fields appear on screen.
              </p>
              <Button variant="primary" className="mt-1 w-100">
                <FaLayerGroup className="me-2" />
                Manage Layouts
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-4">
          <Card className="management-option-card" onClick={() => handleViewChange('recordviews')}>
            <Card.Body className="text-center py-3">
              <div className="management-icon-wrapper">
                <FaTags size={40} className="text-primary" />
              </div>
              <h5 className="mt-2 mb-2">Record view management</h5>
              <p className="text-muted small mb-2 px-1">
                Record view types, labels, and defaults for this object.
              </p>
              <Button variant="primary" className="mt-1 w-100">
                <FaTags className="me-2" />
                Manage record views
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-4">
          <Card className="management-option-card" onClick={() => handleViewChange('actions')}>
            <Card.Body className="text-center py-3">
              <div className="management-icon-wrapper">
                <FaBolt size={40} className="text-primary" />
              </div>
              <h5 className="mt-2 mb-2">Action Management</h5>
              <p className="text-muted small mb-2 px-1">
                Custom actions, detail buttons, and related workflows.
              </p>
              <Button variant="primary" className="mt-1 w-100">
                <FaBolt className="me-2" />
                Manage Actions
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-4">
          <Card
            className="management-option-card"
            onClick={() => handleViewChange('validations')}
          >
            <Card.Body className="text-center py-3">
              <div className="management-icon-wrapper">
                <FaShieldAlt size={40} className="text-primary" />
              </div>
              <h5 className="mt-2 mb-2">Validation rules</h5>
              <p className="text-muted small mb-2 px-1">
                Rules on save: JSON conditions and error messages for users.
              </p>
              <Button variant="primary" className="mt-1 w-100">
                <FaShieldAlt className="me-2" />
                Manage validations
              </Button>
            </Card.Body>
          </Card>
        </Col>

        {['opportunity', 'quote', 'order'].includes(
          (selectedObject?.object_name || '').toLowerCase()
        ) && (
            <Col lg={3} md={6} className="mb-4">
              <Card
                className="management-option-card"
                onClick={() => handleViewChange('lineitem-wizard')}
              >
                <Card.Body className="text-center py-3">
                  <div className="management-icon-wrapper">
                    <FaThList size={40} className="text-primary" />
                  </div>
                  <h5 className="mt-2 mb-2">Line items wizard</h5>
                  <p className="text-muted small mb-2 px-1">
                    Add-products modal: search columns, pricing step, and labels.
                  </p>
                  <Button variant="primary" className="mt-1 w-100">
                    <FaThList className="me-2" />
                    Configure line items
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          )}
      </Row>
    </div>
  );

  const renderFieldsView = () => (
    <div className="fields-view">
      <FieldManager
        objectName={selectedObject.object_name}
        objectId={selectedObject.id}
        show={true}
        onHide={() => {
          // When FieldManager is hidden, go back to object details
          handleViewChange('object-details');
        }}
        onNavigate={handleViewChange}
        selectedObject={selectedObject}
      />
      {/* Debug logging */}
      {console.log('ObjectManager - selectedObject.id:', selectedObject.id)}
      {console.log('ObjectManager - selectedObject.id type:', typeof selectedObject.id)}
      {console.log('ObjectManager - selectedObject.id length:', selectedObject.id ? selectedObject.id.length : 'undefined')}
    </div>
  );

  const renderLayoutsView = () => (
    <div className="layouts-view">

      <div className="view-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => handleViewChange('object-details')}
              className="mb-3"
            >
              ← Back to Object
            </Button>

          </div>
        </div>
      </div>
      <LayoutManager
        objectName={selectedObject.object_name}
        initialLayoutId={pendingLayoutOpenId}
        onInitialLayoutOpened={() => {
          setPendingLayoutOpenId(null);
          const url = new URL(window.location);
          if (url.searchParams.has('layoutId')) {
            url.searchParams.delete('layoutId');
            window.history.replaceState({}, '', url);
          }
        }}
      />
    </div>
  );

  const renderRolesView = () => (
    <div className="roles-view">
      <div className="view-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => handleViewChange('object-details')}
              className="mb-3"
            >
              ← Back to Object
            </Button>
            <h2>
              <FaUsers className="me-3" />
              Role Management - {selectedObject.object_label || selectedObject.object_name}
            </h2>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowRoleManager(true)}
          >
            <FaPlus className="me-1" />
            Assign Roles
          </Button>
        </div>
      </div>

      <RoleManager
        objectName={selectedObject.object_name}
        show={showRoleManager}
        onHide={() => setShowRoleManager(false)}
      />
    </div>
  );

  const renderActionsView = () => (
    <div className="actions-view">
      <div className="view-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => handleViewChange('object-details')}
              className="mb-3"
            >
              ← Back to Object
            </Button>
            <h2>
              <FaBolt className="me-3" />
              Action Management - {selectedObject.object_label || selectedObject.object_name}
            </h2>
          </div>
        </div>
      </div>

      <ActionManager
        objectName={selectedObject.object_name}
        show={true}
        onHide={() => {
          // When ActionManager is hidden, go back to object details
          handleViewChange('object-details');
        }}
      />
    </div>
  );

  const renderRecordViewsView = () => (
    <div className="recordviews-view">
      <RecordViewManager
        objectName={selectedObject.object_name}
        onBack={() => handleViewChange('object-details')}
      />
    </div>
  );

  const renderValidationsView = () => (
    <div className="validations-view">
      <ObjectValidationManager
        objectName={selectedObject.object_name}
        objectLabel={selectedObject.object_label}
        onBack={() => handleViewChange('object-details')}
      />
    </div>
  );

  const renderLineItemWizardView = () => (
    <div className="lineitem-wizard-settings-view">
      <LineItemWizardConfigPanel
        objectName={selectedObject.object_name}
        objectLabel={selectedObject.object_label}
        onBack={() => handleViewChange('object-details')}
      />
    </div>
  );

  return (
    <div className="object-manager" style={{ padding: 0, margin: 0, width: '100%' }}>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {restoringFromUrl && (
        <Alert variant="info" dismissible onClose={() => setRestoringFromUrl(false)}>
          Restoring state from URL parameters...
        </Alert>
      )}

      {activeView === 'objects' && renderObjectsView()}
      {activeView === 'object-details' && selectedObject && renderObjectDetailsView()}
      {activeView === 'fields' && selectedObject && renderFieldsView()}
      {activeView === 'layouts' && selectedObject && renderLayoutsView()}
      {activeView === 'recordviews' && selectedObject && renderRecordViewsView()}
      {activeView === 'validations' && selectedObject && renderValidationsView()}
      {activeView === 'lineitem-wizard' && selectedObject && renderLineItemWizardView()}
      {activeView === 'actions' && selectedObject && renderActionsView()}

      {/* EditObject Modal */}
      <EditObject
        show={showEditObject}
        onHide={() => setShowEditObject(false)}
        mode={editObjectMode}
        objectData={editObjectData}
        onSuccess={handleEditObjectSuccess}
      />

      {selectedObject && (
        <PublishStandardObjectModal
          show={showPublishModal}
          onHide={() => setShowPublishModal(false)}
          object={selectedObject}
          onSuccess={handlePublishSuccess}
        />
      )}

      <DeleteConfirmationModal
        show={showObjectTrashModal}
        onHide={resetObjectTrashModal}
        onConfirm={confirmTrashObject}
        title="Move Object to Trash"
        blockedTitle="Object In Use"
        message="Are you sure you want to move this object to trash?"
        itemName={objectToTrash}
        confirmText="Move to Trash"
        cancelText="Cancel"
        variant="warning"
        isChecking={objectTrashModalChecking}
        checkingMessage="Checking if this object can be deleted..."
        isDeleting={objectTrashModalDeleting}
        loadingMessage="Moving object to trash..."
        resultMessage={objectTrashModalError}
        blockedContent={
          objectTrashModalBlockedReferences
            ? renderObjectReferenceSections(objectTrashModalBlockedReferences, objectToTrash || 'This object')
            : null
        }
      />

      <DeleteConfirmationModal
        show={showObjectPermanentDeleteModal}
        onHide={resetObjectPermanentDeleteModal}
        onConfirm={confirmPermanentlyDeleteObject}
        title="Permanently Delete Object"
        blockedTitle="Object In Use"
        message="Are you sure you want to permanently delete this object? This action cannot be undone and will remove the table completely."
        itemName={objectToPermanentlyDelete}
        confirmText="Delete Permanently"
        cancelText="Cancel"
        isChecking={objectPermanentDeleteModalChecking}
        checkingMessage="Checking if this object can be deleted..."
        isDeleting={objectPermanentDeleteModalDeleting}
        loadingMessage="Permanently deleting object..."
        resultMessage={objectPermanentDeleteModalError}
        blockedContent={
          objectPermanentDeleteModalBlockedReferences
            ? renderObjectReferenceSections(
              objectPermanentDeleteModalBlockedReferences,
              objectToPermanentlyDelete || 'This object'
            )
            : null
        }
      />

      {/* Trash Modal */}
      <Modal show={showTrashModal} onHide={() => setShowTrashModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTrash className="me-2 text-danger" />
            Trashed Objects
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingTrashedObjects ? (
            <div className="text-center py-4">
              <div className="loading-spinner mb-3"></div>
              <p>Loading trashed objects...</p>
            </div>
          ) : trashedObjects.length === 0 ? (
            <div className="text-center py-4">
              <FaTrash className="text-muted mb-3" style={{ fontSize: '3rem' }} />
              <h5 className="text-muted">No trashed objects</h5>
              <p className="text-muted">Objects you move to trash will appear here</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle">
                <thead>
                  <tr>
                    <th>Object Name</th>
                    <th>Object Label</th>
                    <th>Trashed Date</th>
                    <th style={{ width: 200 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trashedObjects.map((object) => (
                    <tr key={object.id}>
                      <td>
                        <code>{object.object_name}</code>
                      </td>
                      <td>
                        {object.object_label || object.object_name}
                      </td>
                      <td>
                        {object.lastmodifieddate ? new Date(object.lastmodifieddate).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() => handleRestoreObject(object.object_name)}
                          >
                            Restore
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={isObjectDeleteBusy}
                            onClick={() => handlePermanentlyDeleteObject(object.object_name)}
                          >
                            Delete Permanently
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTrashModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ObjectManager;
